/**
 * ScheduleReleaseStep3
 * Step 3: Confirm and review summary before creating slots
 *
 * @version 2025.1.0
 */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  CalendarMonth,
  EventAvailable,
  EventBusy,
} from '@mui/icons-material';
import {
  ScheduleReleaseSummaryResponse,
  HolidayDecision,
  formatPeriod,
  formatHours,
} from '../../../services/scheduleReleaseService';

interface ScheduleReleaseStep3Props {
  summary: ScheduleReleaseSummaryResponse;
  holidayDecisions: HolidayDecision[];
}

const ScheduleReleaseStep3: React.FC<ScheduleReleaseStep3Props> = ({
  summary,
  holidayDecisions,
}) => {
  const workedHolidays = holidayDecisions.filter((d) => d.workOnHoliday);
  const skippedHolidays = holidayDecisions.filter((d) => !d.workOnHoliday);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirmar Liberação
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Revise o resumo abaixo e clique em "Confirmar e Liberar" para criar os slots.
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <CalendarMonth color="primary" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h5">{summary.totals.totalDays}</Typography>
          <Typography variant="caption" color="text.secondary">Dias Úteis</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <Schedule color="info" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h5">{formatHours(summary.totals.totalHours)}</Typography>
          <Typography variant="caption" color="text.secondary">Total de Horas</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <EventAvailable color="success" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h5">{summary.totals.totalSlots}</Typography>
          <Typography variant="caption" color="text.secondary">Slots a Criar</Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <EventBusy color="warning" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h5">{summary.totals.holidaysSkipped}</Typography>
          <Typography variant="caption" color="text.secondary">Feriados Pulados</Typography>
        </Paper>
      </Box>

      {/* Period Info */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Período
        </Typography>
        <Typography variant="body2">
          {formatPeriod(summary.period.startDate, summary.period.endDate)}
          {' '}({summary.period.totalDays} dias corridos)
        </Typography>
      </Paper>

      {/* Holiday Decisions Summary */}
      {holidayDecisions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Decisões sobre Feriados
          </Typography>

          {workedHolidays.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Trabalhar em:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {workedHolidays.map((holiday) => (
                  <Chip
                    key={holiday.holidayId}
                    label={`${holiday.name} (${new Date(holiday.date).toLocaleDateString('pt-BR')})`}
                    color="success"
                    size="small"
                    icon={<CheckCircle />}
                  />
                ))}
              </Box>
            </Box>
          )}

          {skippedHolidays.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Folgar em:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {skippedHolidays.map((holiday) => (
                  <Chip
                    key={holiday.holidayId}
                    label={`${holiday.name} (${new Date(holiday.date).toLocaleDateString('pt-BR')})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />

      <Alert severity="success" icon={<CheckCircle />}>
        <Typography variant="body2">
          <strong>Tudo pronto!</strong> Ao confirmar, serão criados <strong>{summary.totals.totalSlots} slots</strong> de
          atendimento para o período selecionado.
        </Typography>
      </Alert>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Atenção:</strong> Esta ação não pode ser desfeita automaticamente.
          Os slots criados poderão ser bloqueados ou excluídos individualmente depois.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ScheduleReleaseStep3;
